<!-- src/views/StockReportsView.vue -->
<template>
  <div class="container coredeck-page" style="max-width: 1400px; margin-top: 24px">
    <!-- =====================================================
         PAGE HEADER
         ===================================================== -->
    <div class="page-header">
      <div>
        <h4 style="margin: 0">Stock Reports</h4>

        <p class="grey-text text-darken-1" style="margin: 6px 0 0 0">
          Completed stock counts from the last 13 weeks.
        </p>
      </div>

      <button
        class="btn-flat waves-effect refresh-button"
        type="button"
        :disabled="loading"
        @click="loadReports"
      >
        <i class="material-icons left"> refresh </i>

        Refresh
      </button>
    </div>

    <!-- =====================================================
         NO ACTIVE VENUE
         ===================================================== -->
    <div v-if="!activeVenueId" class="card">
      <div class="card-content no-venue">
        <i class="material-icons"> info_outline </i>

        <span> No active venue is available. </span>
      </div>
    </div>

    <template v-else>
      <!-- ===================================================
           ERROR
           =================================================== -->
      <div v-if="error" class="card error-card">
        <div class="card-content">
          {{ error }}
        </div>
      </div>

      <!-- ===================================================
           LOADING
           =================================================== -->
      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <!-- ===================================================
           EMPTY
           =================================================== -->
      <div v-else-if="!counts.length" class="card empty-card">
        <div class="card-content">
          No completed stock counts were found within the last 13 weeks.
        </div>
      </div>

      <!-- ===================================================
           REPORT LIST

           We retain Materialize's:
             collapsible
             popout

           for the visual styling.

           Vue controls the opening/closing instead of
           M.Collapsible.

           This avoids Materialize leaving .collapsible-body
           permanently display:none.
           =================================================== -->
      <ul v-else class="collapsible popout stock-reports-collapsible">
        <li
          v-for="count in counts"
          :key="count.id"
          :class="{
            active: openCountId === count.id,
          }"
        >
          <!-- =================================================
               REPORT HEADER
               ================================================= -->
          <div
            class="collapsible-header report-header"
            role="button"
            tabindex="0"
            @click="toggleReport(count.id)"
            @keydown.enter.prevent="toggleReport(count.id)"
            @keydown.space.prevent="toggleReport(count.id)"
          >
            <!-- ===============================================
                 CHEVRON
                 =============================================== -->
            <i class="material-icons report-chevron">
              {{ openCountId === count.id ? "expand_less" : "expand_more" }}
            </i>

            <!-- ===============================================
                 DATE
                 =============================================== -->
            <div class="report-header-main">
              <div class="report-date">
                Stock Count —
                {{ displayDate(count.completed_at) }}
              </div>

              <div class="report-meta">
                {{ count.lines.length }}
                product{{ count.lines.length === 1 ? "" : "s" }}
              </div>
            </div>

            <!-- ===============================================
                 HEADER VARIANCE
                 =============================================== -->
            <div
              class="header-variance"
              :class="varianceClass(count.report.totals.variance_value)"
            >
              <span> Variance </span>

              <strong>
                {{ money(count.report.totals.variance_value) }}
              </strong>
            </div>
          </div>

          <!-- =================================================
               REPORT BODY

               IMPORTANT:
               Materialize CSS normally sets collapsible-body
               to display:none.

               report-body-open explicitly overrides that.
               ================================================= -->
          <div
            v-if="openCountId === count.id"
            class="collapsible-body report-body report-body-open"
          >
            <!-- ===============================================
                 REPORT TITLE / STATUS
                 =============================================== -->
            <div class="report-body-header">
              <div>
                <h5 class="report-body-title">Completed Stock Count</h5>

                <div class="grey-text text-darken-1">
                  Finalised
                  {{ displayDate(count.completed_at) }}
                </div>
              </div>

              <span class="finalised-chip">
                <i class="material-icons"> lock </i>

                Finalised
              </span>
            </div>

            <!-- ===============================================
                 SUMMARY REPORT

                 This component uses buildStockReportData(),
                 which is the same calculation engine as
                 the PDF.
                 =============================================== -->
            <StockReportsSummaryCard :count="count" />

            <!-- ===============================================
                 PDF ACTION
                 =============================================== -->
            <div class="report-actions">
              <button
                class="btn teal darken-2 waves-effect waves-light"
                type="button"
                :disabled="!!pdfBusyId"
                @click.stop="downloadPdf(count)"
              >
                <i class="material-icons left"> picture_as_pdf </i>

                {{ pdfBusyId === count.id ? "Generating..." : "Download PDF" }}
              </button>

              <!-- =============================================
                   PDF PROGRESS
                   ============================================= -->
              <div v-if="pdfBusyId === count.id" class="pdf-progress-area">
                <span class="pdf-progress-text">
                  {{ pdfMessage }}
                </span>

                <div class="progress pdf-progress-bar">
                  <div
                    class="determinate"
                    :style="{
                      width: pdfProgress + '%',
                    }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- ===============================================
                 DETAIL HEADING
                 =============================================== -->
            <div class="detail-heading-row">
              <div>
                <h6 class="detail-heading">Stock Detail</h6>

                <span class="grey-text text-darken-1">
                  Physical stock movement and variance by product.
                </span>
              </div>
            </div>

            <!-- ===============================================
                 DETAIL TABLE
                 =============================================== -->
            <div class="report-detail-wrap">
              <table class="striped highlight report-detail-table">
                <thead>
                  <tr>
                    <th>Product</th>

                    <th>Group</th>

                    <th>Supplier</th>

                    <th>Unit</th>

                    <th class="right-align">Cost</th>

                    <th class="right-align">Opening</th>

                    <th class="right-align">Stock In</th>

                    <th class="right-align">Stock Out</th>

                    <th class="right-align">Closing</th>

                    <th class="right-align">Usage</th>

                    <th class="right-align">Till Sales</th>

                    <th class="right-align">Sales Stock</th>

                    <th class="right-align">Var Units</th>

                    <th class="right-align">Var £</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="item in count.report.items"
                    :key="item.product_key || item.product_id"
                  >
                    <!-- PRODUCT -->
                    <td class="product-name">
                      {{ item.product_name }}
                    </td>

                    <!-- GROUP -->
                    <td>
                      {{ item.product_group }}
                    </td>

                    <!-- SUPPLIER -->
                    <td>
                      {{ item.supplier }}
                    </td>

                    <!-- UNIT -->
                    <td>
                      {{ item.unit_size }}
                    </td>

                    <!-- COST -->
                    <td class="right-align">
                      {{ money(item.unit_cost) }}
                    </td>

                    <!-- OPENING -->
                    <td class="right-align">
                      {{ number(item.opening_count) }}
                    </td>

                    <!-- STOCK IN -->
                    <td class="right-align">
                      {{ number(item.stock_in) }}
                    </td>

                    <!-- STOCK OUT -->
                    <td class="right-align">
                      {{ number(item.stock_out) }}
                    </td>

                    <!-- CLOSING -->
                    <td class="right-align">
                      {{ number(item.closing_count) }}
                    </td>

                    <!-- CONSUMPTION -->
                    <td class="right-align">
                      {{ number(item.consumption) }}
                    </td>

                    <!-- RAW POS SALES -->
                    <td
                      class="right-align"
                      :title="`${number(item.sales_stock_units)} physical stock units`"
                    >
                      {{ number(item.sales) }}
                    </td>

                    <!-- CONVERTED SALES -->
                    <td
                      class="right-align"
                      :title="`${number(item.sales)} till selling units`"
                    >
                      {{ number(item.sales_stock_units) }}
                    </td>

                    <!-- VAR UNITS -->
                    <td
                      class="right-align variance-value"
                      :class="varianceTextClass(item.variance_units)"
                    >
                      {{ number(item.variance_units) }}
                    </td>

                    <!-- VAR £ -->
                    <td
                      class="right-align variance-value"
                      :class="varianceTextClass(item.variance_value)"
                    >
                      {{ money(item.variance_value) }}
                    </td>
                  </tr>
                </tbody>

                <!-- ===========================================
                     TOTALS
                     =========================================== -->
                <tfoot>
                  <tr class="totals-row">
                    <td colspan="5" class="totals-label">Totals</td>

                    <td class="right-align">
                      {{ number(count.report.totals.opening_units) }}
                    </td>

                    <td class="right-align">
                      {{ number(count.report.totals.stock_in_units) }}
                    </td>

                    <td class="right-align">
                      {{ number(count.report.totals.stock_out_units) }}
                    </td>

                    <td class="right-align">
                      {{ number(count.report.totals.closing_units) }}
                    </td>

                    <td class="right-align">
                      {{ number(count.report.totals.consumption_units) }}
                    </td>

                    <td class="right-align">—</td>

                    <td class="right-align">
                      {{ number(count.report.totals.sales_stock_units) }}
                    </td>

                    <td
                      class="right-align"
                      :class="varianceTextClass(count.report.totals.variance_units)"
                    >
                      {{ number(count.report.totals.variance_units) }}
                    </td>

                    <td
                      class="right-align variance-value"
                      :class="varianceTextClass(count.report.totals.variance_value)"
                    >
                      {{ money(count.report.totals.variance_value) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- ===============================================
                 VARIANCE KEY
                 =============================================== -->
            <div class="variance-key">
              <span class="green-text text-darken-2"> Positive = Stock Gain </span>

              <span class="red-text text-darken-2"> Negative = Stock Missing </span>
            </div>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { fetchCompletedStockCountsLast13Weeks } from "@/scripts/sqlApiHelper/stockCountApi";

import {
  buildStockPdfReport,
  buildStockReportData,
  stockReportDate,
} from "@/scripts/stockPdfReport";

import StockReportsSummaryCard from "@/components/stock/StockReportsSummaryCard.vue";

export default {
  name: "StockReportsView",

  components: {
    StockReportsSummaryCard,
  },

  /* =========================================================
     VENUE CONTEXT
     ========================================================= */

  setup() {
    const { venueId } = useVenueContext();

    return {
      venueContextId: venueId,
    };
  },

  data() {
    return {
      /* -----------------------------------------------------
         COMPLETED COUNTS
         ----------------------------------------------------- */

      counts: [],

      /* -----------------------------------------------------
         WHICH REPORT IS OPEN
         ----------------------------------------------------- */

      openCountId: "",

      /* -----------------------------------------------------
         PAGE STATE
         ----------------------------------------------------- */

      loading: false,

      error: null,

      /* -----------------------------------------------------
         PDF STATE
         ----------------------------------------------------- */

      pdfBusyId: "",

      pdfProgress: 0,

      pdfMessage: "",
    };
  },

  computed: {
    /* =====================================================
       ACTIVE VENUE
       ===================================================== */

    activeVenueId() {
      return String(this.venueContextId ?? "").trim();
    },
  },

  watch: {
    /* =====================================================
       VENUE CHANGE
       ===================================================== */

    activeVenueId: {
      immediate: true,

      async handler() {
        await this.loadReports();
      },
    },
  },

  methods: {
    /* =====================================================
       LOAD COMPLETED STOCK REPORTS
       ===================================================== */

    async loadReports() {
      this.counts = [];

      this.openCountId = "";

      this.error = null;

      if (!this.activeVenueId) {
        return;
      }

      this.loading = true;

      try {
        const completedCounts = await fetchCompletedStockCountsLast13Weeks(
          this.activeVenueId
        );

        /* -------------------------------------------------
           BUILD REPORT DATA

           The report is generated from the saved,
           finalised count lines.

           There is no separate report document required.
           ------------------------------------------------- */

        this.counts = (Array.isArray(completedCounts) ? completedCounts : []).map(
          (count) => {
            const lines = Array.isArray(count.lines) ? count.lines : [];

            return {
              ...count,

              lines,

              report: buildStockReportData({
                rows: lines,

                turnover: count.turnover || null,
              }),
            };
          }
        );

        /* -------------------------------------------------
           AUTO-OPEN THE MOST RECENT COMPLETED COUNT

           So a report is immediately visible on the page.
           ------------------------------------------------- */

        if (this.counts.length) {
          this.openCountId = this.counts[0].id;
        }
      } catch (e) {
        console.error("[STOCK REPORTS] Load failed:", e);

        this.error = e?.message || "Unable to load stock reports.";
      } finally {
        this.loading = false;
      }
    },

    /* =====================================================
       TOGGLE REPORT

       Only one report can be open at once.
       ===================================================== */

    toggleReport(countId) {
      if (this.openCountId === countId) {
        this.openCountId = "";

        return;
      }

      this.openCountId = countId;
    },

    /* =====================================================
       GENERATE PDF
       ===================================================== */

    async downloadPdf(count) {
      if (!count?.id || this.pdfBusyId) {
        return;
      }

      this.pdfBusyId = count.id;

      this.pdfProgress = 0;

      this.pdfMessage = "Preparing...";

      this.error = null;

      try {
        const { doc, filename } = await buildStockPdfReport(
          {
            rows: Array.isArray(count.lines) ? count.lines : [],

            venueId: this.activeVenueId,

            venueName: count.venue_name || "",

            completedAt: count.completed_at,

            countId: count.id,

            turnover: count.turnover || null,
          },

          (progress, message) => {
            this.pdfProgress = progress;

            this.pdfMessage = message;
          }
        );

        doc.save(filename);
      } catch (e) {
        console.error("[STOCK REPORTS] PDF failed:", e);

        this.error = e?.message || "Unable to generate stock PDF.";
      } finally {
        this.pdfBusyId = "";

        this.pdfProgress = 0;

        this.pdfMessage = "";
      }
    },

    /* =====================================================
       DATE
       ===================================================== */

    displayDate(value) {
      return stockReportDate(value);
    },

    /* =====================================================
       NUMBER
       ===================================================== */

    safeNumber(value) {
      const n = Number(value);

      return Number.isFinite(n) ? n : 0;
    },

    number(value) {
      return this.safeNumber(value).toLocaleString("en-GB", {
        minimumFractionDigits: 2,

        maximumFractionDigits: 2,
      });
    },

    /* =====================================================
       MONEY
       ===================================================== */

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",

        currency: "GBP",

        minimumFractionDigits: 2,

        maximumFractionDigits: 2,
      }).format(this.safeNumber(value));
    },

    /* =====================================================
       HEADER VARIANCE CLASS
       ===================================================== */

    varianceClass(value) {
      const n = this.safeNumber(value);

      if (n < 0) {
        return "variance-negative";
      }

      if (n > 0) {
        return "variance-positive";
      }

      return "";
    },

    /* =====================================================
       DETAIL VARIANCE CLASS
       ===================================================== */

    varianceTextClass(value) {
      const n = this.safeNumber(value);

      if (n < 0) {
        return "red-text text-darken-2";
      }

      if (n > 0) {
        return "green-text text-darken-2";
      }

      return "";
    },
  },
};
</script>

<style scoped>
/* =========================================================
   PAGE
   ========================================================= */

.coredeck-page {
  width: 90%;
}

.page-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 16px;

  margin-bottom: 18px;
}

.refresh-button {
  flex: 0 0 auto;
}

/* =========================================================
   STATES
   ========================================================= */

.no-venue {
  display: flex;

  align-items: center;

  gap: 8px;
}

.error-card {
  border: 1px solid #ef9a9a;

  color: #c62828;
}

.empty-card {
  text-align: center;

  color: #757575;
}

/* =========================================================
   POPOUT REPORT LIST
   ========================================================= */

.stock-reports-collapsible {
  margin-top: 0;
}

.stock-reports-collapsible > li {
  margin-bottom: 10px;

  border-radius: 8px;

  overflow: hidden;

  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.stock-reports-collapsible > li.active {
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.18);
}

/* =========================================================
   REPORT HEADER
   ========================================================= */

.report-header {
  display: flex;

  align-items: center;

  min-height: 68px;

  gap: 10px;

  cursor: pointer;

  user-select: none;
}

.report-header:hover {
  background: #fafafa;
}

.report-chevron {
  flex: 0 0 auto;

  color: #546e7a;
}

.report-header-main {
  flex: 1 1 auto;

  min-width: 0;
}

.report-date {
  color: #263238;

  font-size: 1rem;

  font-weight: 600;
}

.report-meta {
  margin-top: 2px;

  color: #78909c;

  font-size: 0.75rem;
}

/* =========================================================
   HEADER VARIANCE
   ========================================================= */

.header-variance {
  flex: 0 0 auto;

  display: flex;

  flex-direction: column;

  align-items: flex-end;

  gap: 2px;

  min-width: 110px;
}

.header-variance span {
  color: #78909c;

  font-size: 0.7rem;
}

.header-variance strong {
  color: #455a64;

  font-size: 1rem;
}

.variance-positive strong {
  color: #2e7d32;
}

.variance-negative strong {
  color: #c62828;
}

/* =========================================================
   REPORT BODY

   THIS OVERRIDES MATERIALIZE:

   .collapsible-body {
       display: none;
   }

   Without this, the report exists but remains invisible.
   ========================================================= */

.stock-reports-collapsible .report-body-open {
  display: block !important;

  padding: 22px;

  border-top: 1px solid #eceff1;

  background: #ffffff;
}

/* =========================================================
   REPORT BODY HEADER
   ========================================================= */

.report-body-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 16px;

  margin-bottom: 18px;
}

.report-body-title {
  margin: 0 0 3px 0;

  color: #263238;

  font-weight: 600;
}

/* =========================================================
   FINALISED CHIP
   ========================================================= */

.finalised-chip {
  display: inline-flex;

  align-items: center;

  gap: 4px;

  min-height: 28px;

  padding: 0 10px;

  border-radius: 14px;

  background: #e8f5e9;

  color: #2e7d32;

  font-size: 0.75rem;

  font-weight: 600;
}

.finalised-chip i {
  font-size: 15px;
}

/* =========================================================
   REPORT ACTIONS
   ========================================================= */

.report-actions {
  display: flex;

  align-items: center;

  gap: 12px;

  margin-top: 20px;

  margin-bottom: 22px;
}

.pdf-progress-area {
  flex: 1 1 auto;

  max-width: 300px;
}

.pdf-progress-text {
  display: block;

  margin-bottom: 4px;

  color: #607d8b;

  font-size: 0.8rem;
}

.pdf-progress-bar {
  margin: 0;
}

/* =========================================================
   DETAIL HEADING
   ========================================================= */

.detail-heading-row {
  margin-bottom: 10px;
}

.detail-heading {
  margin: 0 0 3px 0;

  color: #263238;

  font-weight: 600;
}

/* =========================================================
   DETAIL TABLE
   ========================================================= */

.report-detail-wrap {
  width: 100%;

  overflow-x: auto;
}

.report-detail-table {
  width: 100%;

  min-width: 1100px;
}

.report-detail-table th {
  color: #455a64;

  font-size: 0.75rem;

  font-weight: 600;

  white-space: nowrap;
}

.report-detail-table td {
  font-size: 0.78rem;

  vertical-align: middle;

  white-space: nowrap;
}

.product-name {
  font-weight: 600;
}

.variance-value {
  font-weight: 700;
}

/* =========================================================
   TOTALS
   ========================================================= */

.totals-row {
  border-top: 2px solid #607d8b;

  background: #eceff1;
}

.totals-row td {
  font-weight: 700;
}

.totals-label {
  text-align: right;
}

/* =========================================================
   VARIANCE KEY
   ========================================================= */

.variance-key {
  display: flex;

  align-items: center;

  justify-content: flex-end;

  gap: 20px;

  margin-top: 14px;

  font-size: 0.78rem;

  font-weight: 600;
}

/* =========================================================
   MOBILE
   ========================================================= */

@media only screen and (max-width: 600px) {
  .coredeck-page {
    width: 94%;
  }

  .page-header {
    display: block;
  }

  .page-header .btn-flat {
    margin-top: 8px;
  }

  .report-header {
    padding-left: 10px;

    padding-right: 10px;
  }

  .report-date {
    font-size: 0.88rem;
  }

  .header-variance {
    min-width: 85px;
  }

  .stock-reports-collapsible .report-body-open {
    padding: 12px;
  }

  .report-body-header {
    align-items: flex-start;
  }

  .report-actions {
    display: block;
  }

  .report-actions .btn {
    width: 100%;
  }

  .pdf-progress-area {
    max-width: none;

    margin-top: 8px;
  }

  .variance-key {
    display: block;

    text-align: right;
  }

  .variance-key span {
    display: block;

    margin-top: 4px;
  }
}
</style>
